import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Item, Menu, Separator, Trigger } from './styles';

export interface IRowAction {
  /** Texto do item; é também o que o leitor de tela anuncia. */
  label: string;
  onSelect: () => void;
  /** Ação de risco: texto vermelho, separada e sempre no fim da lista. */
  destructive?: boolean;
}

interface IRowActionsProps {
  actions: IRowAction[];
  /** Diz de qual linha são as ações, ex.: "Ações de Gigatech". */
  label: string;
}

interface IPosition {
  top: number;
  right: number;
  /** Abre para cima quando falta espaço embaixo do gatilho. */
  above: boolean;
}

const GAP = 6;
/** Altura estimada do menu, só para decidir se abre para cima. */
const ITEM_HEIGHT = 44;

/**
 * Menu "⋯" com as ações secundárias de uma linha (desclassificar, remover…).
 *
 * Tira das listas a ação destrutiva repetida em toda linha: ela continua a
 * um toque de distância, mas para de disputar atenção com o conteúdo.
 *
 * Renderiza num portal com posição fixa porque as tabelas do torneio vivem
 * dentro de wrappers com `overflow`, que cortariam um menu `absolute` nas
 * últimas linhas.
 */
const RowActions: React.FC<IRowActionsProps> = ({ actions, label }) => {
  const [position, setPosition] = useState<IPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isOpen = position !== null;

  const ordered = [
    ...actions.filter(a => !a.destructive),
    ...actions.filter(a => a.destructive),
  ];
  const firstDestructive = ordered.findIndex(a => a.destructive);

  const close = useCallback((returnFocus = false) => {
    setPosition(null);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const open = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const menuHeight = ordered.length * ITEM_HEIGHT + 8;
    const above =
      rect.bottom + GAP + menuHeight > window.innerHeight &&
      rect.top - GAP - menuHeight > 0;
    setPosition({
      top: above ? rect.top - GAP : rect.bottom + GAP,
      right: Math.max(8, window.innerWidth - rect.right),
      above,
    });
  };

  // Foco no primeiro item ao abrir, para o teclado seguir direto.
  useLayoutEffect(() => {
    if (isOpen) {
      menuRef.current
        ?.querySelector<HTMLButtonElement>('[role="menuitem"]')
        ?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !menuRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        close();
      }
    };
    // Posição fixa não acompanha a rolagem: fechar é mais honesto que
    // deixar o menu flutuando longe da linha.
    const handleScroll = () => close();

    document.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen, close]);

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>(
        '[role="menuitem"]',
      ) ?? [],
    );
    const index = items.indexOf(document.activeElement as HTMLButtonElement);

    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
    } else if (event.key === 'Tab') {
      close();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      items[(index + step + items.length) % items.length]?.focus();
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      items[event.key === 'Home' ? 0 : items.length - 1]?.focus();
    }
  };

  if (ordered.length === 0) return null;

  return (
    <>
      <Trigger
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => (isOpen ? close() : open())}
      >
        <svg viewBox="0 0 24 24" width="1.15rem" height="1.15rem" aria-hidden>
          <circle cx="5" cy="12" r="1.8" fill="currentColor" />
          <circle cx="12" cy="12" r="1.8" fill="currentColor" />
          <circle cx="19" cy="12" r="1.8" fill="currentColor" />
        </svg>
      </Trigger>

      {position &&
        createPortal(
          <Menu
            ref={menuRef}
            role="menu"
            aria-label={label}
            $above={position.above}
            style={{ top: position.top, right: position.right }}
            onKeyDown={handleMenuKeyDown}
          >
            {ordered.map((action, i) => (
              <React.Fragment key={action.label}>
                {i === firstDestructive && i > 0 && <Separator />}
                <Item
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  $destructive={!!action.destructive}
                  onClick={() => {
                    close(true);
                    action.onSelect();
                  }}
                >
                  {action.label}
                </Item>
              </React.Fragment>
            ))}
          </Menu>,
          document.body,
        )}
    </>
  );
};

export default RowActions;
